"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, Gauge, ShieldCheck, Zap, ArrowRight, CircleCheck as CheckCircle2, FileSpreadsheet, Layers, Thermometer } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const processHighlights = [{
  icon: Flame,
  title: "Atomização homogénea de enxofre líquido",
  description: "Gotículas de diâmetro consistente asseguram a queima completa e imediata do enxofre na câmara de combustão, evitando a formação de depósitos de ácido ou cinzas não queimadas."
}, {
  icon: Thermometer,
  title: "Lanças com camisa de vapor térmico",
  description: "Controlo rigoroso da temperatura nas lanças de injeção para prevenir a solidificação do enxofre ou a corrosão ácida por condensação a jusante."
}, {
  icon: Gauge,
  title: "Maior conversão em SO2 / SO3",
  description: "A distribuição precisa do jato de pulverização maximiza a eficiência da reação de contacto nos leitos catalíticos e estabiliza a geração de vapor de alta pressão."
}, {
  icon: Zap,
  title: "Eficiência energética e redução de emissões",
  description: "Diminuição comprovada do excesso de ar necessário para a combustão, reduzindo as emissões de gases poluentes e otimizando o balanço térmico global da fábrica."
}];
const technicalSpecs = [{
  label: "Gama de caudais operacionais",
  value: "20 a 4.500 kg/h por bico"
}, {
  label: "Temperaturas de operação",
  value: "Até 1.400 °C na fornalha"
}, {
  label: "Materiais resistentes à corrosão",
  value: "Hastelloy, Inconel e Aço Inox 316L"
}, {
  label: "Tecnologia de ponta Spraying Systems",
  value: "Bicos CBA e WhirlJet com camisa de vapor"
}];
const successPoints = ["Uniformidade no espectro de gotas para queima rápida", "Manutenção simplificada com fechos de desengate rápido", "Dimensionamento CFD personalizado para a geometria do forno", "Suporte técnico em Portugal e peças de substituição dedicadas"];
export default function SulfurBurning() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  return <section id="sulfurburningoverview" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 font-medium">
            <Layers className="size-3.5 text-secondary" />
            Engenharia Química e Metalurgia
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Queima de enxofre e produção de ácido sulfúrico
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Sistemas de injeção de alta precisão desenvolvidos para maximizar a
            conversão térmica, garantir a estabilidade do forno e eliminar
            paragens inesperadas em unidades de processamento contínuo.
          </p>
        </motion.div>

        {/* Process Highlights Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {processHighlights.map((item, i) => {
          const Icon = item.icon;
          return <div data-index={i} key={item.title} className="h-full">
                <Card className="h-full border border-border bg-card shadow-xs transition-[box-shadow,border-color] duration-200 hover:border-secondary/40 hover:shadow-md">
                  <CardHeader className="pb-3">
                    <div className="mb-3 flex size-12 items-center justify-center rounded-lg bg-accent text-secondary">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </motion.div>

        {/* Main Content Showcase */}
        <div className="mt-16 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          {/* Left Column: Technical Context */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="lg:col-span-7">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ShieldCheck className="size-4 text-secondary" />
                Tecnologia Spraying Systems Co.
              </div>

              <h3 className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Controlo térmico e atomização hidráulica sem entupimentos
              </h3>

              <p className="text-base leading-relaxed text-muted-foreground">
                A combustão eficiente do enxofre líquido depende da rápida evaporação
                e mistura com o ar pré-aquecido. A Eurocontrol projeta lanças
                customizadas e bicos de atomização hidráulica ou com vapor de alta
                pressão que mantêm um padrão de pulverização rigoroso, eliminando o
                risco de impacto de líquido nas paredes refratárias do forno.
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 pt-2">
                {successPoints.map((point, i) => <div data-index={i} key={point} className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-secondary" />
                    <span className="text-sm font-medium text-foreground">
                      {point}
                    </span>
                  </div>)}
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                  <Link to="/ContactsPage">
                    Solicitar estudo técnico
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/ChemicalProcessingCaseStudyPage">
                    <FileSpreadsheet className="size-4" />
                    Ver caso de estudo
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Key Specifications Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-md sm:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <h4 className="text-lg font-semibold text-card-foreground">
                    Parâmetros de Desempenho
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Sistemas industriais para fábricas de ácido
                  </p>
                </div>
                <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">
                  ISO 9001
                </span>
              </div>

              <div className="divide-y divide-border pt-4">
                {technicalSpecs.map((spec, i) => <div data-index={i} key={spec.label} className="flex flex-col py-3.5 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      {spec.label}
                    </span>
                    <span className="mt-1 text-sm font-semibold text-foreground sm:mt-0">
                      {spec.value}
                    </span>
                  </div>)}
              </div>

              <Separator className="my-4" />

              <div className="rounded-lg bg-muted/60 p-4">
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Precisa de uma substituição urgente de bicos ou de uma lança com
                  camisa de vapor adaptada à sua flange? A nossa equipa de
                  engenharia efetua o cálculo fluídico exato para a sua instalação.
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">
                    Representação exclusiva em Portugal
                  </span>
                  <Link to="/ProductsPage" className="text-xs font-semibold text-secondary hover:underline">
                    Explorar produtos
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}